import { StoreyViewsPlugin } from './StoreyViewsPlugin';

/**
 * @desc Information about an ````IfcBuildingStorey````.
 *
 * These are provided by a {@link StoreyViewsPlugin}.
 */
class Storey {
  /**
   * @private
   */

  plugin: StoreyViewsPlugin;

  storeyId: string;
  modelId: string | number;
  modelAABB: number[];
  storeyAABB: number[];
  storeyObjectsAABB: number[];
  numObjects: number;
  name: string;
  elevation?: number;

  constructor(
    plugin: StoreyViewsPlugin,
    modelAABB: number[],
    storeyAABB: number[],
    storeyObjectsAABB: number[],
    modelId: string | number,
    storeyId: string,
    numObjects: number,
    name: string,
    elevation?: number,
  ) {
    /**
     * The {@link StoreyViewsPlugin} this Storey belongs to.
     *
     * @property plugin
     * @type {StoreyViewsPlugin}
     */
    this.plugin = plugin;

    /**
     * ID of the IfcBuildingStorey.
     *
     * This matches IDs of the IfcBuildingStorey's {@link MetaObject} and {@link Entity}.
     *
     * @property storeyId
     * @type {String}
     */
    this.storeyId = storeyId;

    /**
     * ID of the model.
     *
     * This matches the ID of the {@link MetaModel} that contains the IfcBuildingStorey's {@link MetaObject}.
     *
     * @property modelId
     * @type {String|Number}
     */
    this.modelId = modelId;

    /**
     * Axis-aligned World-space boundary of the {@link Entity}s that represent the IfcBuildingStorey - clipped based on 'elevation' attribute if it exists.
     *
     * The boundary is a six-element Float64Array containing the min/max extents of the
     * axis-aligned boundary, ie. ````[xmin, ymin, zmin, xmax, ymax, zmax]````
     *
     * @property storeyAABB
     * @type {number[]}
     */
    this.storeyAABB = [...storeyAABB];

    /**
     * Axis-aligned World-space boundary of the {@link Entity}s that represent the IfcBuildingStorey.
     *
     * The boundary is a six-element Float64Array containing the min/max extents of the
     * axis-aligned boundary, ie. ````[xmin, ymin, zmin, xmax, ymax, zmax]````
     *
     * @property storeyAABB
     * @type {Number[]}
     */
    this.storeyObjectsAABB = storeyObjectsAABB;

    /**
     * Axis-aligned World-space boundary the storey maps are framed on: the union of every
     * storey in the model, which excludes model geometry that sits far from the building.
     *
     * The boundary is a six-element Float64Array containing the min/max extents of the
     * axis-aligned boundary, ie. ````[xmin, ymin, zmin, xmax, ymax, zmax]````
     *
     * @property modelAABB
     * @type {Number[]}
     */
    this.modelAABB = [...modelAABB];

    /** Number of {@link Entity}s within the IfcBuildingStorey.
     *
     * @property numObjects
     * @type {Number}
     */
    this.numObjects = numObjects;

    this.name = name;

    this.elevation = elevation;
  }
}

export { Storey };
